import { useCreateNewSimpleAssetRegister } from "hooks/createEntity";
import type {
  AssetRegisterItem,
  DeviceConfigurationOutput,
  GenericCSVFieldMapping,
  SFTPReceiverParamsInput,
  SimpleAssetDefinition,
} from "kubb";
import { sftpParserTypeEnum, statusEnum } from "kubb";
import AssetRegisterList from "pages/assetRegister/AssetRegisterListPage/AssetRegisterList";
import AssetRegisterListItem from "pages/assetRegister/AssetRegisterListPage/AssetRegisterListItem";
import { useState } from "react";
import ObjectPropertyItem from "../../../components/Headers/ObjectPropertyItem";
import AssetRegisterStatusLabel from "../../../components/Labels/AssetRegisterStatusLabel";
import ObjectDetailHeading from "../../../components/Layout/ObjectDetailHeading";
import RoutedTabGroupInterface from "../../../components/TabGroup/RoutedTabGroupInterface";
import PrivacyMode from "../../../components/Text/PrivacyMode";
import { Heading } from "../../../components/Theme/heading";
import { assetPositionToBreadcrumb } from "../../../components/navigation/breadcrumbs/Breadcrumbs";
import CopyToClipboard from "../../../components/utils/CopyToClipboard";
import { formatDate } from "../../../components/utils/HighlightNullAndOldDates";
import { OneCardPage } from "../../../components/utils/OneCardPage";
import EventList from "../../../containers/event/EventList";
import NoteList from "../../../containers/notes/NotesList";
import type { AssetRegisterEntitySuccessProps } from "../../../core/action/GetAssetRegisterEntitiesCombination";
import AssetPositionDeleteTab from "../assetPositions/tabs/AssetPositionDeleteTab";
import GatewayFilelist from "../assets/gateway/GatewayFileList/GatewayFilelist";
import GenericCSVAssetUpload from "./GenericCSVAssetUpload";
import IntegrationConfigurationTab from "./IntegrationConfigurationTab";

function isGenericCsvIntegration(
  integration: Record<string, unknown> | undefined,
): boolean {
  if (!integration) return false;
  const params = integration as SFTPReceiverParamsInput;
  return (
    params.integration_type === "sftp_receiver" &&
    params.parser_type === sftpParserTypeEnum.generic_csv
  );
}

export default function IntegrationHomePage(props: {
  integration: AssetRegisterEntitySuccessProps;
  deleteAssetPosition: () => Promise<void>;
}) {
  const { assetPosition, location, loading, refresh } = props.integration;
  const { deleteAssetPosition } = props;

  const deleted = assetPosition?.status === statusEnum.DELETED;
  const isGenericCsv = isGenericCsvIntegration(
    assetPosition?.configuration?.integration as
      | Record<string, unknown>
      | undefined,
  );

  const tabs = [
    {
      tabName: "Configuration",
      tabPath: "configuration",
      tabContent: assetPosition ? (
        <IntegrationConfigurationTab
          assetPosition={assetPosition}
          refresh={refresh}
        />
      ) : (
        <></>
      ),
    },
    {
      tabName: "Files",
      tabContent: assetPosition ? (
        <GatewayFilelist assetPosition={assetPosition} />
      ) : (
        <></>
      ),
      tabPath: "files",
      keepSearchString: false,
    },
    {
      tabName: "Owned Asset Positions",
      tabPath: "positions",
      tabContent: assetPosition ? (
        <GenericCSVOwnedPositions
          assetPosition={assetPosition}
          showDetailToggle={isGenericCsv}
        />
      ) : (
        <></>
      ),
    },
    ...(isGenericCsv && assetPosition
      ? [
          {
            tabName: "Add Assets",
            tabPath: "add-assets",
            tabContent: (
              <GenericCSVUploadTab
                assetPosition={assetPosition}
                refresh={refresh}
              />
            ),
            userTypes: "editor" as const,
          },
        ]
      : []),
    {
      tabName: "Notes",
      tabPath: "note",
      tabContent: assetPosition ? (
        <>
          <Heading>Notes</Heading>
          <NoteList assetPosition={assetPosition} />
        </>
      ) : (
        <></>
      ),
    },
    {
      tabName: "Events",
      tabPath: "events",
      tabContent: assetPosition ? (
        <>
          <Heading>Asset Position Events</Heading>
          <EventList
            asset_position_id={assetPosition.asset_position_id}
            company_id={assetPosition.company_id}
          />
        </>
      ) : (
        <></>
      ),
    },
    {
      tabName: "Delete",
      tabPath: "delete",
      tabContent: assetPosition ? (
        <AssetPositionDeleteTab
          assetPosition={assetPosition}
          delete={deleteAssetPosition}
          title={"Asset Position"}
        />
      ) : (
        <></>
      ),
      userTypes: "admin" as const,
    },
  ];

  return (
    <OneCardPage
      headerTitle={"Integration Homepage"}
      breadcrumbs={
        assetPosition && location && [assetPositionToBreadcrumb(assetPosition)]
      }
      loading={loading}
      setPageTitle={() =>
        `Integration${location ? `: ${location.address}` : ""}`
      }
    >
      {assetPosition && location && (
        <>
          <ObjectDetailHeading
            leftColumn={{
              content: (
                <>
                  <ObjectPropertyItem
                    title={"Integration Title"}
                    type={"heading"}
                  >
                    <PrivacyMode>{location?.address}</PrivacyMode>
                  </ObjectPropertyItem>
                  <ObjectPropertyItem
                    title={"Integration Type"}
                    type={"heading"}
                  >
                    <PrivacyMode>
                      {
                        assetPosition?.configuration?.integration
                          ?.integration_type
                      }
                    </PrivacyMode>
                  </ObjectPropertyItem>
                </>
              ),
            }}
            rightColumn={{
              content: (
                <>
                  {deleted && (
                    <ObjectPropertyItem title={"Status"}>
                      <AssetRegisterStatusLabel status={assetPosition.status} />
                    </ObjectPropertyItem>
                  )}
                  <ObjectPropertyItem title={"Asset Position ID"}>
                    <CopyToClipboard
                      value={assetPosition?.asset_position_id}
                      slice={8}
                    />
                  </ObjectPropertyItem>
                  <ObjectPropertyItem title={"Created"}>
                    {formatDate(assetPosition?.created_at, undefined, false)}
                  </ObjectPropertyItem>
                  <ObjectPropertyItem title={"Updated"}>
                    {formatDate(assetPosition?.last_updated, undefined, false)}
                  </ObjectPropertyItem>
                </>
              ),
            }}
          />
          <RoutedTabGroupInterface
            tabs={tabs}
            topLevelPath={`/admin/asset_register/asset_position/${assetPosition.asset_position_id}`}
          />
        </>
      )}
    </OneCardPage>
  );
}

/* ── Upload Tab (generic CSV only) ── */

function GenericCSVUploadTab(props: {
  assetPosition: NonNullable<AssetRegisterEntitySuccessProps["assetPosition"]>;
  refresh?: () => void;
}) {
  const { assetPosition, refresh } = props;
  const create = useCreateNewSimpleAssetRegister(assetPosition);

  const handleUpload = async (
    definitions: SimpleAssetDefinition[],
  ): Promise<void> => {
    await create.update(definitions);
    refresh?.();
  };

  return (
    <>
      <Heading>Add Assets from CSV</Heading>
      <GenericCSVAssetUpload
        onUpload={handleUpload}
        loading={create.loading}
        error={create.error}
      />
    </>
  );
}

/* ── Owned Positions Tab ── */

function GenericCSVOwnedPositions(props: {
  assetPosition: NonNullable<AssetRegisterEntitySuccessProps["assetPosition"]>;
  showDetailToggle: boolean;
}) {
  const { assetPosition, showDetailToggle } = props;
  const [showDetail, setShowDetail] = useState(false);

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <Heading>Owned Asset Positions</Heading>
        {showDetailToggle && (
          <label className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400 cursor-pointer">
            <input
              type="checkbox"
              checked={showDetail}
              onChange={(e) => setShowDetail(e.target.checked)}
              className="rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
            />
            Show field details
          </label>
        )}
      </div>

      <AssetRegisterList
        params={{
          parent_asset_position_id: assetPosition.asset_position_id,
        }}
        company_id={assetPosition.company_id}
        scheme_id={assetPosition.scheme_id}
        createAssetTags={false}
        row={
          showDetail
            ? (rowProps) => (
                <AssetRowWithFieldDetail
                  assetRegisterItem={rowProps.assetRegisterItem}
                  showScheme={rowProps.showScheme}
                />
              )
            : undefined
        }
      />
    </>
  );
}

/* ── Detail Row with field mappings column ── */

function FieldMappingSummary({ fields }: { fields: GenericCSVFieldMapping[] }) {
  if (!fields || fields.length === 0) {
    return (
      <span className="text-zinc-400 dark:text-zinc-500 italic">
        No field mappings
      </span>
    );
  }
  return (
    <div className="space-y-0.5">
      {fields.map((f) => (
        <div key={f.identifier_value}>
          <span className="font-medium">{f.standardised_column}</span>
          <span className="text-zinc-400 dark:text-zinc-500">
            {" : "}
            {f.identifier_value} : {f.units}
          </span>
        </div>
      ))}
    </div>
  );
}

function AssetRowWithFieldDetail(props: {
  assetRegisterItem: AssetRegisterItem;
  showScheme?: boolean;
}) {
  const { asset } = props.assetRegisterItem;
  const config = asset?.configuration as DeviceConfigurationOutput | undefined;
  const fields = (config?.generic_csv?.fields ??
    []) as GenericCSVFieldMapping[];

  return (
    <AssetRegisterListItem
      assetRegisterItem={props.assetRegisterItem}
      showScheme={props.showScheme}
      extensionSmall={
        fields.length > 0 ? (
          <div
            className="mt-1 text-xs text-zinc-500 dark:text-zinc-400"
            style={{ fontFamily: "monospace" }}
          >
            <FieldMappingSummary fields={fields} />
          </div>
        ) : undefined
      }
    />
  );
}
